import { useState, useMemo, useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import { ImageIcon } from '@/components/icons';
import { usePersistentCategories } from '../../hooks/usePublishedContent';
import styles from './MegaMenuPanel.module.css';

/** Maps URL section param (plural) to API section param (singular) */
const sectionApiMap: Record<string, string> = {
  brands: 'brand',
  products: 'product',
  experiences: 'experience',
  // Spec 022 — assets share the same plural-to-singular convention as
  // every other section. Without this entry the megamenu queries
  // `usePersistentCategories('assets', …)` which never matches because
  // asset categories are stored with section='asset', and the panel
  // renders "No categories yet" even when there are published assets.
  assets: 'asset',
};

interface MegaMenuPanelProps {
  section: string;           // URL param: 'brands' | 'products' | 'experiences' | 'assets'
  // Spec 022 — pass the full category object so the parent can route
  // by id (asset library uses ?categoryId=) or by name (guideline
  // library uses ?category=). Without the id, the asset library
  // would have to resolve the name back to an id at navigation time.
  onCategoryClick: (category: { id: string; name: string }) => void;
}

export function MegaMenuPanel({
  section,
  onCategoryClick,
}: MegaMenuPanelProps) {
  const apiSection = sectionApiMap[section] ?? section;
  const { data: categories, isLoading } = usePersistentCategories(apiSection);

  const [hoveredCategoryId, setHoveredCategoryId] = useState<string | null>(null);

  // Default to first category if nothing hovered yet
  const activeCategoryId = hoveredCategoryId ?? categories?.[0]?.id ?? null;

  const activeCategory = useMemo(() => {
    return categories?.find((c) => c.id === activeCategoryId) ?? null;
  }, [categories, activeCategoryId]);

  const handleCategoryHover = useCallback((id: string) => {
    setHoveredCategoryId(id);
  }, []);

  // Sliding highlight: measure the active item and animate the indicator
  // between positions. Initial render starts faded out so the first frame
  // doesn't slide in from (0,0); we fade it in once we have a measurement.
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [highlight, setHighlight] = useState<{ top: number; height: number; visible: boolean }>(
    { top: 0, height: 0, visible: false }
  );
  useLayoutEffect(() => {
    if (!categories || !activeCategoryId) {
      setHighlight((s) => ({ ...s, visible: false }));
      return;
    }
    const idx = categories.findIndex((c) => c.id === activeCategoryId);
    const el = itemRefs.current[idx];
    if (!el) return;
    setHighlight({ top: el.offsetTop, height: el.offsetHeight, visible: true });
  }, [activeCategoryId, categories]);

  // Hairline scroll rail + sliding marker — mirrors the LibrarySidebar
  // treatment so both list surfaces share one scroll vocabulary. Position
  // math runs imperatively on each scroll event to avoid re-rendering the
  // category list.
  const scrollRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const scroll = scrollRef.current;
    const rail = railRef.current;
    const indicator = indicatorRef.current;
    if (!scroll || !rail || !indicator) return;

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = scroll;
      const railHeight = rail.clientHeight;
      if (scrollHeight <= clientHeight || railHeight === 0) {
        indicator.style.opacity = '0';
        indicator.style.height = '0px';
        return;
      }
      const minIndicatorH = 24;
      const rawIndicatorH = (clientHeight / scrollHeight) * railHeight;
      const indicatorH = Math.max(rawIndicatorH, minIndicatorH);
      const maxTop = railHeight - indicatorH;
      const ratio = scrollTop / (scrollHeight - clientHeight);
      const top = ratio * maxTop;
      indicator.style.opacity = '1';
      indicator.style.height = `${indicatorH}px`;
      indicator.style.transform = `translateY(${top}px)`;
    };

    update();
    scroll.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(scroll);
    if (scroll.firstElementChild) ro.observe(scroll.firstElementChild);

    return () => {
      scroll.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [categories?.length]);

  if (isLoading) {
    return (
      <div className={styles.panel}>
        <div className={styles.emptyState}>Loading…</div>
      </div>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <div className={styles.panel}>
        <div className={styles.emptyState}>No categories yet</div>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      {/* Left column: categories */}
      <div className={`${styles.column} ${styles.columnLeft}`}>
        {/* data-lenis-prevent: on the homepage, Lenis runs in `root`
            mode and captures wheel events document-wide — without this
            it would scroll the page instead of this category list. */}
        <div
          ref={scrollRef}
          className={styles.columnLeftScroll}
          data-lenis-prevent
        >
          {/* Sliding highlight — single absolute element that animates between
              the active item's top/height. */}
          <div
            className={styles.categoryHighlight}
            style={{
              transform: `translateY(${highlight.top}px)`,
              height: `${highlight.height}px`,
              opacity: highlight.visible ? 1 : 0,
            }}
          />
          {categories.map((cat, i) => (
            <div
              key={cat.id}
              ref={(el) => { itemRefs.current[i] = el; }}
              className={`${styles.categoryItem} ${cat.id === activeCategoryId ? styles.categoryItemActive : ''}`}
              onMouseEnter={() => handleCategoryHover(cat.id)}
              onClick={() => onCategoryClick({ id: cat.id, name: cat.name })}
            >
              {cat.name}
            </div>
          ))}
        </div>
        <div ref={railRef} className={styles.scrollRail} aria-hidden />
        <div ref={indicatorRef} className={styles.scrollIndicator} aria-hidden />
      </div>

      {/* Right column: category thumbnail or placeholder */}
      <div className={`${styles.column} ${styles.columnImage}`}>
        {activeCategory?.coverImage ? (
          <img
            src={activeCategory.coverImage}
            alt=""
            className={styles.categoryThumbnail}
          />
        ) : (
          <div className={styles.imagePlaceholder}>
            <ImageIcon width={32} height={32} />
          </div>
        )}
      </div>
    </div>
  );
}
